import { Box, Button, Chip, IconButton, Tooltip } from "@mui/material";
import {
  MaterialReactTable,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";
import React, {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { NEW_UNIQUE_ROW_ID, translateTable } from "../../Common";
import { ReactComponent as AddIcon } from "../../assets/table/add.svg";
import { ReactComponent as CloseIcon } from "../../assets/table/close.svg";
import { ReactComponent as DeleteIcon } from "../../assets/table/delete.svg";
import { ReactComponent as EditIcon } from "../../assets/table/edit.svg";
import { ReactComponent as LaunchIcon } from "../../assets/table/external-link.svg";
import { ReactComponent as SaveIcon } from "../../assets/table/save.svg";
import { ReactComponent as CopyIdIcon } from "../../assets/idCopy.svg";
import KeyIcon from "@mui/icons-material/Key";
import ApiIntakeModal from "../clientsApiIntake/ApiIntakeModal";
import useAuthenticatedFetch from "../../auth/authenticated";
import { common, neutral } from "../../theme/colors";
import { FEEDBACK } from "../../models/Feedback";
import CellRenderer from "./CellRenderer";
import { generateColumns } from "./GenerateColumns";
import handleCancelRow from "./handleCancelRow";
import handleCreateRow from "./handleCreateRow";
import { useThemeContext } from "../../theme/ThemeContextProvider";
import Feedback from "../ui/Feedback";
import { RowData, TableColumn } from "../../models/Table";
import DetailPanel from "./DetailPanel";

/** Admin CRUD tables: list payload is slim; full row via GET {endpoint}/{id} on expand. */
const LAZY_DETAIL_TABLE_IDS = new Set([
  "tenants",
  "mails",
  "users",
  "carriers",
  "clients",
  "warehouses",
  "settings",
]);

/**
 * Required-field emptiness for Admin table save.
 * Numeric 0 and boolean false are valid values; only null, undefined, and blank strings are missing.
 */
const isBlankTableValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  return false;
};

const Table = ({
  initialData,
  columnData,
  endpoint,
  showCreateNewButton,
  showExtraActions,
  id,
  claimDetailsPreloaded = false,
  minTableHeight = "500px",
  showToolbar = true,
  showBottomToolbar = true,
  enableColumnFilters = true,
  enableSorting = true,
  enablePagination = true,
}) => {
  const getAccessHeader = useAuthenticatedFetch();
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const tableBackgroundStyle = {
    backgroundColor: mode === "light" ? common["white"] : neutral[900],
  };

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [flattenedData, setFlattenedData] = useState<any>([]);
  const [initialFlattenedData, setInitialFlattenedData] = useState<any>([]);
  const [editableRowId, setEditableRowId] = useState<number | null>(null);
  const [editedFields, setEditedFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});
  const [detailColumns, setDetailColumns] = useState<TableColumn[]>([]);

  const [showFeedback, setShowFeedback] = useState<FEEDBACK>(FEEDBACK.NONE);
  const [customErrorMessage, setCustomErrorMessage] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [apiIntakeClient, setApiIntakeClient] = useState<any | null>(null);

  const getErrorMessage = (result: any, fallback: string) => {
    return result?.error || result?.detail?.error || fallback;
  };

  /**
   * Generate the initial rows data once
   */
  useEffect(() => {
    // Flatten the unpleasant API response structure of each object in the array
    if (initialData.length > 0) {
      // Flatten the data by iterating over each item in the initialData array
      const flattenedData = initialData.map((item) => {
        const flattened = flatItem(item);
        if (claimDetailsPreloaded && id === "claims") {
          flattened.claimDetailsLoaded = true;
        }
        return flattened;
      });

      // save as stable reference in state
      setFlattenedData(flattenedData);
      // Set the initial flattened data state to the same array for resetting purposes after editing
      setInitialFlattenedData(flattenedData);
    }
  }, [initialData, claimDetailsPreloaded, id]);

  /**
   * Maps the Backend data to a flattened object
   * @param item
   */
  const flatItem = (item: RowData) : any => {
    // Early exit if item is null or undefined
    if (!item) return {};
    
    // Create a new object for the flattened data
    const flattenedItem = {};

    // Add properties from entry if it exists
    if (item.entry) {
      Object.assign(flattenedItem, item.entry);
    }

    // Add properties from on_delete_info if it exists
    if (item.on_delete_info) {
      Object.assign(flattenedItem, item.on_delete_info);
    }

    return flattenedItem;
  }

  /**
   * Updates the claim data object with the complete data properties
   * @param response
   * @param rowIndex
   */
  const addCompleteClaimData = (response, rowIndex) => {
    if (!response.entries?.length) {
      return;
    }

    const flattenedClaim = flatItem(response.entries[0]);
    // add indicator to prevent api call again
    flattenedClaim.claimDetailsLoaded = true;

    setFlattenedData((prevData) => {
        const newData = [...prevData];
        // Update the specific row with a new object holding the detail values
        newData[rowIndex] = flattenedClaim;

        return newData;
    });

    // Also update the initial data for resetting purposes after editing
    setInitialFlattenedData((prevData) => {
      const newData = [...prevData];
      newData[rowIndex] = flattenedClaim;

      return newData;
    });
  }

  const mergeRowDetailIntoState = (detail: Record<string, unknown>, rowIndex: number) => {
    setFlattenedData((prevData) => {
      const newData = [...prevData];
      newData[rowIndex] = { ...newData[rowIndex], ...detail, detailsLoaded: true };
      return newData;
    });
    setInitialFlattenedData((prevData) => {
      const newData = [...prevData];
      newData[rowIndex] = { ...newData[rowIndex], ...detail, detailsLoaded: true };
      return newData;
    });
  };

  /**
   * Requests all fields of a claim from API and updates the existing data
   * @param row
   */
  const fetchClaimDetailData = async (row) => {
    if (id !== "claims") {
      return;
    }
    if (flattenedData[row.id]?.claimDetailsLoaded) {
      return;
    }

    try {
      const response = await fetch("/api/claims?filter=id:" + row.original.id, {
        method: "GET",
        headers: await getAccessHeader(false),
      });

      if (!response.ok) {
        console.error("Error fetching claim details");
        return;
      }

      const data = await response.json();
      addCompleteClaimData(data, row.id);
    } catch (error) {
      console.error("Failed to fetch claim details:", error);
    }
  };

  const fetchRowDetailData = async (row) => {
    if (!LAZY_DETAIL_TABLE_IDS.has(id)) {
      return;
    }
    if (flattenedData[row.id]?.detailsLoaded) {
      return;
    }

    try {
      const response = await fetch(`${endpoint}/${row.original.id}`, {
        method: "GET",
        headers: await getAccessHeader(false),
      });

      if (!response.ok) {
        console.error("Error fetching row details");
        return;
      }

      const detail = await response.json();
      mergeRowDetailIntoState(detail, row.id);
    } catch (error) {
      console.error("Failed to fetch row details:", error);
    }
  };

  const loadRowDetailIfNeeded = (row) => {
    if (id === "claims") {
      return fetchClaimDetailData(row);
    }
    if (LAZY_DETAIL_TABLE_IDS.has(id)) {
      return fetchRowDetailData(row);
    }
    return Promise.resolve();
  };

  const handleEditRowClick = (row) => {
    loadRowDetailIfNeeded(row).then();

    // Set the row being edited by updating the editableRowId state
    setEditableRowId(row.original.id);

    // Expand the row in the table to show its details
    table.setExpanded({ [row.id]: true });
  };

  const handleCancelRowClick = (row) => {
    setFieldErrors({});

    handleCancelRow(
        row,
        initialFlattenedData,
        setFlattenedData,
        setEditableRowId,
        setEditedFields,
        table,
     ).then();
  };

  const handleCreateRowClick = () => {
    setFieldErrors({});

    handleCreateRow(
      setFlattenedData,
      setEditableRowId,
      columns,
      table,
    );
  };

  const validateRow = (row) => {
    const errors: { [key: string]: boolean } = {};

    columns.forEach((column) => {
      if (!column.nullable && isBlankTableValue(row.original[column.accessorKey])) {
        errors[column.accessorKey] = true;
      }
    });

    setFieldErrors(errors);

    return errors;
  };

  const handleSaveRowClick = async (row) => {
    const invalidColumns = validateRow(row);

    if (Object.keys(invalidColumns).length > 0) {
      setShowFeedback(FEEDBACK.ERROR);
      setCustomErrorMessage(t("GENERAL.ERROR_VALIDATION"));
      console.log("Validation errors:", invalidColumns);

      return;
    }

    setCustomErrorMessage(undefined);

    // update data model
    try {
      let response;
      // Row is being edited, perform PUT request
      if (row.original.id !== NEW_UNIQUE_ROW_ID) {
        // Include the id field from the original row along with editedFields
        const editedFieldsWithId = { ...editedFields, id: row.original.id };
        const isTenantPushSettingsTable = id === "tenantPushSettings";
        const url = isTenantPushSettingsTable ? endpoint : `${endpoint}/${row.original.id}`;
        const body = isTenantPushSettingsTable ? editedFields : editedFieldsWithId;

        response = await fetch(url, {
          method: "PUT",
          headers: await getAccessHeader(),
          body: JSON.stringify(body),
        });

        const result = await response.json();

        if (result.success) {
          // Update initial data and empty edited fields state
          setInitialFlattenedData(flattenedData);
          setEditedFields({});
          setShowFeedback(FEEDBACK.SUCCESS);
        } else {
          const errorMessage = getErrorMessage(result, "Unknown error");
          setShowFeedback(FEEDBACK.ERROR);
          setCustomErrorMessage("Failed to save: " + errorMessage);
          console.error("Failed to save row:", errorMessage);
          // close opened edited row to get old values back into view
          handleCancelRowClick(row);
        }
      } else {
        // New row, perform POST request
        response = await fetch(endpoint, {
          method: "POST",
          headers: await getAccessHeader(),
          body: JSON.stringify(editedFields),
        });

        const result = await response.json();

        if (result.success) {
          row.original.id = result.id;
          setShowFeedback(FEEDBACK.SUCCESS);
          setEditedFields({});
        } else {
          const errorMessage = getErrorMessage(result, "Unknown error");
          setShowFeedback(FEEDBACK.ERROR);
          setCustomErrorMessage("Failed to save: " + errorMessage);
          console.error("Failed to save row:", errorMessage);
          // remove temp new row from view
          handleCancelRowClick(row);
        }
      }

      // reset and close
      table.setCreatingRow(null);
      setEditableRowId(null);
      table.setExpanded({ [row.id]: false });
    } catch (error) {
      console.error("Error saving row:", error);
    }
  };

  // update data model
  const handleDeleteRowClick = (row) => {
    handleDeleteRow(
      row.original.id,
      endpoint,
      setFlattenedData,
    ).then(() => setInitialFlattenedData(flattenedData));
  };

  const handleDeleteRow = async (rowId, endpoint, setFlattenedData) => {
    try {
      const response = await fetch(`${endpoint}/${rowId}`, {
        method: "DELETE",
        headers: await getAccessHeader(false),
      });

      const result = await response.json();

      if (result.success) {
        setFlattenedData((prevRowData) =>
          prevRowData.filter((row) => row.id !== rowId),
        );

        setShowFeedback(FEEDBACK.SUCCESS);
      } else {
        const errorMessage = getErrorMessage(result, "Unknown error");
        setShowFeedback(FEEDBACK.ERROR);
        setCustomErrorMessage("Failed to delete: " + errorMessage);
        console.error(`Failed to delete row with ID ${rowId}.`);
        return;
      }
    } catch (error) {
      setShowFeedback(FEEDBACK.ERROR);
      console.error("Error deleting row:", error);
    }
  };

  const handleDragEnd =
    (
      table,
      flattenedData,
      setFlattenedData,
      endpoint,
    ) =>
    async () => {
      const { draggingRow, hoveredRow } = table.getState();

      if (hoveredRow && draggingRow) {
        const newFlattenedData = [...flattenedData];
        const draggedRow = newFlattenedData.splice(draggingRow.index, 1)[0];
        newFlattenedData.splice(hoveredRow.index, 0, draggedRow);

        try {
          const response = await fetch(
            `${endpoint}/${hoveredRow.original.id}`,
            {
              method: "PUT",
              headers: await getAccessHeader(),
              body: JSON.stringify({
                order_number: hoveredRow.index,
                id: draggedRow.id,
              }),
            },
          );

          const result = await response.json();

          if (result.success) {
            setFlattenedData(newFlattenedData);
            setShowFeedback(FEEDBACK.SUCCESS);
          } else {
            const errorMessage = getErrorMessage(result, "Unknown error");
            setShowFeedback(FEEDBACK.ERROR);
            setCustomErrorMessage("Failed to save new order: " + errorMessage);
            console.error("Failed to save new order", errorMessage);
          }
        } catch (error) {
          setShowFeedback(FEEDBACK.ERROR);
          console.error(`Error updating row order number: ${error}`);
        }
      }
    };

  const handleOpenClaimView = (row) => {
    window.open("/claims/" + row.original.id, "_blank");
  };

  const handleCopyClaimId = (row) => {
    navigator.clipboard.writeText(row.original.id).then();
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  /**
   * Only used on mail templates table. Visualizes the enabled status.
   * @param row
   */
  const renderEnabledStatus = (row) => {
    const enabled: boolean = row.original.enabled;
    return (
      <Tooltip title={t(enabled ? "TABLE.ACTIVE" : "TABLE.INACTIVE")}>
        <Chip
          color={enabled ? "success" : "error"}
          size="small"
          sx={{ marginTop: "12px", marginRight: "8px", display: "block", height: "16px" }}
        />
      </Tooltip>
    );
  };

  const handleCellValueChange = (rowIndex: number, columnName: string, value: any) => {
    // reset an eventual error state since the column (cell) has now at least a value
    if (columnName in fieldErrors) {
      const updatedErrors = { ...fieldErrors };
      delete updatedErrors[columnName];

      setFieldErrors(updatedErrors);
    }

    // add neutral timezone to match display from BE data
    if (columnName === 'wakeup_time') {
      if (value) {
        value += 'T00:00:00';
      }
    }

    setEditedFields((prevState) => ({
      ...prevState,
      [columnName]: value,
    }));

    // Update flattenedData to display input changes - optimized to prevent unnecessary re-renders
    setFlattenedData((prevData) => {
        // Only create a new array if we need to update
        const newData = [...prevData];
        // Update the specific row property by creating a new object with the updated value
        // Only update the specific property that changed to minimize re-renders
        newData[rowIndex] = {
          ...newData[rowIndex],
          [columnName]: value,
        };

        return newData;
    });
  };

  // Define the initial table state - like page size and other properties
  const initialStateOptions = {
    columnVisibility: {},
    pagination: { pageSize: 100, pageIndex: 0 },
  };

  // Generate columns based on BE structure and memoize it to improve performance
  const columns = useMemo(() => {
    const columnsToExclude = ["order_number", "id"];
    const result: TableColumn[] = generateColumns(columnData, columnsToExclude, t, id);

    // columns that have to be hidden
    const detailColumns: TableColumn[] = [];
    result.forEach((column) => {
      if (column.renderColumn === false) {
        initialStateOptions.columnVisibility[column.accessorKey] = false;
        detailColumns.push(column);
      }
    });
    setDetailColumns(detailColumns);

    return result;
  }, []);

  // Mapping columns and adding a custom Cell renderer for each column (expensive operation for huge data sets)
  const memoizedColumns = useMemo(() => {
    return columns.map((column) => {
      // Set custom column sizes based on column type or name
      let columnSize: number | undefined = undefined;
      
      // Optimize column sizes for the Claims table
      if (id === 'claims') {
        // Set specific sizes for certain column types or names
        switch(column.accessorKey) {
          case 'tracking_id':
            columnSize = 200;
            break;
          case 'claim_status_id':
            columnSize = 140;
            break;
          case 'client_id':
          case 'carrier_id':
            columnSize = 130;
            break;
          case 'resolution':
          case 'rejection_code':
          case 'stop_code':
          case 'category':
            columnSize = 120;
            break;
          default:
            // Use default size for other columns
            columnSize = undefined;
        }
      }

      return {
        ...column,
        size: columnSize, // Apply custom size if defined
        Cell: ({ row }) => (
            <CellRenderer
                fieldErrors={fieldErrors}
                rowIndex={row.id}
                cellData={row.original[column.accessorKey]}
                column={column}
                isEditMode={editableRowId === row.original.id}
                isDetailPanel={false}
                onCellValueChange={handleCellValueChange}
            />
        ),
      };
    });
  }, [columns, editableRowId, fieldErrors, id]); // Add id dependency


  const table = useMaterialReactTable({
    // Custom representation for the table columns
    columns: memoizedColumns,
    // Data for the table rows
    data: flattenedData,
    // Initial state options like sorting, pagination, etc.
    initialState: initialStateOptions,
    // Enable drag and drop ordering only for the mails & settings table
    enableRowOrdering: id === "mails" || id === "settings",
    // Disable sorting for mail templates to avoid interfering with drag & drop ordering
    enableSorting: enableSorting && id !== "mails",
    enableFilters: enableColumnFilters && id !== "mails",  // Disable filters for mail templates
    enableGlobalFilter: false,      // Disable global search filtering
    enableRowNumbers: true,         // Enable row numbers for the table
    rowNumberDisplayMode: "original",  // Show the original order of rows
    createDisplayMode: "row",       // Row-based display mode for new row creation
    editDisplayMode: "row",         // Row-based display mode for editing
    enableEditing: true,            // Enable editing functionality in the table
    muiPaginationProps: {
    rowsPerPageOptions: [25, 50, 100, 200, 500, 1000], // Add as many as you want
    showFirstButton: true,
    showLastButton: true,
    },
    // Performance optimizations
    enableColumnResizing: false,    // Disable column resizing for better performance
    enableColumnVirtualization: id === 'claims', // Enable column virtualization for claims table
    enableRowVirtualization: id === 'claims',    // Enable row virtualization for claims table
    // Make sure virtualization doesn't affect expanded rows
    layoutMode: 'grid',             // Use grid layout for better handling of expanded rows
    muiTableContainerProps: {
      sx: {
        minHeight: minTableHeight,  // Set configurable minimum table height
        maxWidth: "100%",           // Ensure the table container respects its parent's width
        overflowX: "auto",          // Enable horizontal scrolling
        ...tableBackgroundStyle,    // Apply custom table background style
      },
    },
    defaultColumn: { // applies to all columns
      muiTableHeadCellProps: {
        sx: {
          ...tableBackgroundStyle,    // Apply custom table background style to header
        },
      },
      // Set reasonable default size limits for all columns
      size: id === 'claims' ? 180 : 200, // Make claim table columns more compact
      minSize: 80, // Minimum column width
      maxSize: 300, // Maximum column width
    },
    muiTableHeadCellProps: {
      sx: {
        ...tableBackgroundStyle,    // Fix for the "Expand" column only: background color is not override via "defaultColumn" props - let´s hope the next material version will fix this issue
      },
    },
    muiBottomToolbarProps: {
      sx: {
        ...tableBackgroundStyle,    // Apply custom background style to bottom toolbar
      },
    },
    muiTopToolbarProps: {
      sx: {
        ...tableBackgroundStyle,    // Apply custom background style to top toolbar
      },
    },
    muiTableBodyRowProps: {
      sx: {
        ...tableBackgroundStyle,    // Apply custom background style to body rows
        boxShadow: "unset",         // Remove row box shadow
        height: 'auto', // Fixed height for claims table rows
        '&.Mui-expanded': {
          // Only apply special styling to expanded rows
          height: 'auto',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "1.1px 2.7px 3.8px -1.2px #BBBBBB42"  // Custom box shadow for the table
      }
    },
    enablePagination: enablePagination,          // Enable pagination for table rows
    enableTopToolbar: showToolbar,
    enableBottomToolbar: showBottomToolbar,
    autoResetPageIndex: false,       // Disable auto-reset of page index on changes
    onColumnFiltersChange: setColumnFilters, // Function to handle filter changes
    onSortingChange: setSorting,     // Function to handle sorting changes
    enableStickyHeader: true,        // Keep table header visible while scrolling through the table

    // Render row actions (edit, delete, etc.) conditionally based on the row being edited
    renderRowActions: ({ row }) => (
        <Box sx={{ display: "flex" }}>
          {editableRowId === row.original.id ? (
              <>
                <Tooltip title={t("GENERAL.CANCEL")}>
                  <IconButton onClick={() => handleCancelRowClick(row)}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("GENERAL.SAVE")}>
                  <IconButton onClick={() => handleSaveRowClick(row)}>
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
              </>
          ) : (
              <>
                {showExtraActions && (
                    <>
                      <Tooltip title={t("TABLE.OPEN_CLAIM_VIEW")}>
                        <IconButton onClick={() => handleOpenClaimView(row)}>
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                          title={
                            copied
                                ? t("DASHBOARD.INPUT_COPIED")
                                : t("TABLE.COPY_CLAIM_ID")
                          }
                      >
                        <IconButton onClick={() => handleCopyClaimId(row)}>
                          <CopyIdIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                )}

                {/* Mails Table shows a visual enabled status indicator */}
                {id === 'mails' && (renderEnabledStatus(row))}

                {/* Clients table shows an API-intake provisioning button */}
                {id === 'clients' && (
                  <Tooltip title={t("API_INTAKE.TOOLTIP")}>
                    <IconButton onClick={() => setApiIntakeClient(row.original)}>
                      <KeyIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={t("GENERAL.EDIT")}>
                  <IconButton onClick={() => handleEditRowClick(row)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                {/* Do not show delete button for Claims Table */}
                {id !== 'claims' && id !== 'tenantPushSettings' &&
                  <Tooltip title={!row.original.is_deletable ? t("TABLE.NOT_DELETABLE_INFO") : t("GENERAL.DELETE")}>
                  <span>
                    <IconButton
                        disabled={!row.original.is_deletable}
                        onClick={() => handleDeleteRowClick(row)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                  </Tooltip>
                }
              </>
          )}
        </Box>
    ),

    // Position the actions column at the end of the table
    positionActionsColumn: "last",
    
    // Make actions column sticky so it's always visible
    enableColumnActions: true,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        muiTableHeadCellProps: {
          sx: {
            ...tableBackgroundStyle,
            position: 'sticky',
            right: 0,
            zIndex: 3, // Above other cells
          }
        },
        muiTableBodyCellProps: {
          sx: {
            ...tableBackgroundStyle,
            position: 'sticky',
            right: 0,
            zIndex: 1, // Above other cells
          }
        },
        size: 120, // Fixed width for action column
      },
      'mrt-row-expand': {
        size: 50, // Make the expand column narrower
      },
    },

    // Render custom toolbar actions (like adding a new row)
    renderTopToolbarCustomActions: () =>
        showCreateNewButton ? (
            <Button
                color="primary"
                startIcon={<AddIcon />}
                disabled={editableRowId === NEW_UNIQUE_ROW_ID}  // Disable the button if a new row is being created
                onClick={() => handleCreateRowClick()}
            >
              {t("ADMIN.BUTTON_ADD_NEW")}
            </Button>
        ) : null,

    // Disable expand all functionality
    enableExpandAll: false,

    // Position expand/collapse icons at the last column
    positionExpandColumn: "last",

    // Render a detail panel when a row gets expanded for view or editing.
    // Note: If table data columns amount is enough, the "expand row" button gets rendered as disabled due to null.
    renderDetailPanel: ({ row }) =>
        detailColumns.length > 0 ?
            <DetailPanel
                row={row}
                columns={detailColumns}
                isEditMode={editableRowId === row.original.id}
                fieldErrors={fieldErrors}
                onCellValueChange={handleCellValueChange}
            /> : null,
    // DetailPanel display configuration
    muiDetailPanelProps: {
      sx: {
        padding: '0 16px',
        display: 'block', // Ensure the detail panel uses block display
        backgroundColor: tableBackgroundStyle.backgroundColor,
      },
    },
    muiExpandButtonProps: ({ row }) => ({
      onClick: () => loadRowDetailIfNeeded(row).then(),
    }),
    // Handle row dragging for ordering purposes
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: handleDragEnd(
          table,
          flattenedData,
          setFlattenedData,
          endpoint,
      ),
    }),

    // Define the state for filters and sorting
    state: {
      columnFilters,
      sorting,
    },

    // Localization for the table actions and tooltips
    localization: translateTable(t),
  });

  return (
    <Box>
      <MaterialReactTable table={table} />
      {/* Request Feedback */}
      <Feedback type={showFeedback} onClose={setShowFeedback} errorMessage={customErrorMessage}></Feedback>
      {/* Clients table only: API intake provisioning modal */}
      {apiIntakeClient && (
        <ApiIntakeModal
          open
          onClose={() => setApiIntakeClient(null)}
          client={apiIntakeClient}
          onChanged={() => {
            // Trigger re-fetch by closing; parent component refreshes on tab change.
            // The modal already mutates `current` locally to reflect new state.
          }}
        />
      )}
    </Box>
  );
};

export default Table;
